import React, { useState, useEffect, useMemo } from "react";
import { Image, Tooltip, Progress } from "antd";
import {
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  CloseOutlined,
  AudioOutlined,
  FileZipOutlined,
  VideoCameraOutlined,
  FileMarkdownOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  FileItem,
  FileGalleryProps,
  FileIconType,
  ImageDisplayMode,
  FileCardType,
} from "@/types";
import { useStyles } from "./styles";

// ==================== 工具函数 ====================

const getFileName = (file: FileItem): string =>
  file.displayName || file.name || "";

const getFileSize = (file: FileItem): number => file.size || 0;

const getMimeType = (file: FileItem): string =>
  file.mimeType || file.type || "";

const getFileUrl = (file: FileItem): string =>
  file.fileUri ||
  file.response?.fileUrl ||
  file.response?.tempUrl ||
  file.tempUrl ||
  "";

const getFileId = (file: FileItem): string => file.id || file.uid || "";

const getFileExtension = (filename: string): string => {
  const ext = filename.split(".").pop();
  return ext ? ext.toLowerCase() : "";
};

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
const AUDIO_EXTS = ["mp3", "wav", "m4a", "aac", "ogg", "flac"];
const VIDEO_EXTS = ["mp4", "mov", "webm", "mkv", "avi"];

/** 自动推断文件卡片类型 */
const inferFileType = (file: FileItem): FileCardType => {
  const mimeType = getMimeType(file);
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("video/")) return "video";

  const ext = getFileExtension(getFileName(file));
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (AUDIO_EXTS.includes(ext)) return "audio";
  if (VIDEO_EXTS.includes(ext)) return "video";

  // URL 扩展名兜底
  const url = getFileUrl(file);
  const urlExtMatch = url.match(/\.(\w+)(\?.*)?$/);
  if (urlExtMatch) {
    const urlExt = urlExtMatch[1].toLowerCase();
    if (IMAGE_EXTS.includes(urlExt)) return "image";
    if (AUDIO_EXTS.includes(urlExt)) return "audio";
    if (VIDEO_EXTS.includes(urlExt)) return "video";
  }

  return "file";
};

/** 根据扩展名获取文件图标类型 key */
const getFileIconType = (file: FileItem): FileIconType => {
  const ext = getFileExtension(getFileName(file));
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["ppt", "pptx"].includes(ext)) return "ppt";
  if (AUDIO_EXTS.includes(ext)) return "audio";
  if (VIDEO_EXTS.includes(ext)) return "video";
  if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "tgz"].includes(ext))
    return "zip";
  if (["md", "markdown"].includes(ext)) return "markdown";
  if (IMAGE_EXTS.includes(ext)) return "image";
  return "default";
};

/** 默认图标映射 */
const DEFAULT_ICON_MAP: Record<
  FileIconType,
  { icon: React.ReactNode; color: string }
> = {
  pdf: { icon: <FilePdfOutlined />, color: "#ff4d4f" },
  word: { icon: <FileWordOutlined />, color: "#1677ff" },
  excel: { icon: <FileExcelOutlined />, color: "#22b35e" },
  ppt: { icon: <FilePptOutlined />, color: "#ff6e31" },
  audio: { icon: <AudioOutlined />, color: "#722ed1" },
  video: { icon: <VideoCameraOutlined />, color: "#fa8c16" },
  zip: { icon: <FileZipOutlined />, color: "#eb2f96" },
  markdown: { icon: <FileMarkdownOutlined />, color: "#13c2c2" },
  image: { icon: <FileImageOutlined />, color: "#8c8c8c" },
  default: { icon: <FileOutlined />, color: "#8c8c8c" },
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const FALLBACK_IMAGE =
  "data:image/png;base64,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";

// ==================== 子组件：图片加载状态管理 ====================

const ImageWithLoading: React.FC<{
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  loading?: "lazy" | "eager";
  preview?: false | { src: string };
  fallback?: string;
  displayMode: ImageDisplayMode;
  className?: string;
  styles: ReturnType<typeof useStyles>;
}> = ({
  src,
  alt,
  width,
  height,
  loading,
  preview,
  fallback,
  displayMode,
  className,
  styles,
}) => {
  const [loadState, setLoadState] = useState<
    "loading" | "loaded" | "error"
  >("loading");

  useEffect(() => {
    setLoadState("loading");
  }, [src]);

  const modeClass =
    displayMode === "full"
      ? styles.imageFullMode
      : displayMode === "preview"
        ? styles.imagePreviewMode
        : styles.imageThumbnail;

  const containerStyle: React.CSSProperties = {};
  if (width)
    containerStyle.width = typeof width === "number" ? `${width}px` : width;
  if (height)
    containerStyle.height = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      className={`${modeClass} ${className || ""}`}
      style={containerStyle}
    >
      {loadState === "loading" && (
        <div className={styles.imageLoadingSkeleton}>
          <PictureOutlined style={{ fontSize: 20, color: "#d9d9d9" }} />
        </div>
      )}

      {loadState === "error" && (
        <div className={styles.imageErrorFallback}>
          <PictureOutlined />
          <span>加载失败</span>
        </div>
      )}

      <Image
        src={src}
        alt={alt}
        fallback={fallback}
        preview={preview}
        loading={loading}
        style={{
          display: loadState === "error" ? "none" : "block",
          opacity: loadState === "loading" ? 0 : 1,
          transition: "opacity 0.3s ease",
          ...(displayMode === "thumbnail"
            ? { width: "100%", height: "100%", objectFit: "cover" }
            : { maxWidth: "100%", objectFit: "contain" }),
        }}
        onLoad={() => setLoadState("loaded")}
        onError={() => setLoadState("error")}
      />
    </div>
  );
};

// ==================== 主组件 ====================

const FileGallery: React.FC<FileGalleryProps> = ({
  file,
  align = "left",
  size = "default",
  removable = false,
  onRemove = () => {},
  className,
  style,
  onClick,
  type: customType,
  // 图片相关
  imageDisplayMode = "thumbnail",
  imageWidth,
  imageHeight,
  imageLoading = "eager",
  imageAlt,
  // 音视频相关
  mediaAutoPlay = false,
  mediaLoop = false,
  mediaMuted = false,
  mediaPoster,
  videoProps,
  audioProps,
  // 自定义图标
  iconMap,
  customIcon,
}) => {
  const styles = useStyles();
  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    if (!file?.file) return;
    const url = URL.createObjectURL(file.file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file?.file]);

  if (!file) return null;

  const fileName = getFileName(file);
  const fileSize = getFileSize(file);
  const fileId = getFileId(file);
  const url = getFileUrl(file) || objectUrl;
  const isSmall = size === "small";
  const cardClassName = `${styles.fileCard} ${isSmall ? styles.fileCardSmall : ""} ${align === "right" ? "align-right" : ""} ${className || ""}`;

  // 核心逻辑：type prop 决定渲染方式（参考 file-card）
  // - 不传 type：自动推断，图片/音频/视频用原生渲染
  // - type="file"：强制附件卡片（音视频不可播放）
  // - type="image"/"audio"/"video"：强制指定类型
  const fileType = useMemo<FileCardType>(
    () => customType || inferFileType(file),
    [customType, file],
  );

  /** 获取图标（支持自定义） */
  const getIcon = (
    iconType: FileIconType,
  ): { icon: React.ReactNode; color: string } => {
    if (customIcon) return { icon: customIcon, color: "inherit" };
    if (iconMap?.[iconType])
      return { icon: iconMap[iconType]!, color: "inherit" };
    return DEFAULT_ICON_MAP[iconType];
  };

  // ==================== 渲染删除按钮 ====================
  const renderRemoveBtn = () =>
    removable ? (
      <button
        className={`${styles.fileRemoveBtn} ${isSmall ? styles.fileRemoveBtnSmall : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(fileId);
        }}
        disabled={file.status === "uploading"}
        aria-label="删除文件"
      >
        <CloseOutlined />
      </button>
    ) : null;

  // ==================== 渲染上传进度遮罩 ====================
  const renderUploadOverlay = () =>
    file.status === "uploading" ? (
      <div className={styles.imageOverlay}>
        <Progress
          type="circle"
          percent={file.progress}
          size={30}
          strokeColor="#1677ff"
        />
      </div>
    ) : null;

  // ==================== 附件卡片渲染（type="file" 或普通文档） ====================
  const renderFileCard = () => {
    const iconType = getFileIconType(file);
    const { icon, color } = getIcon(iconType);
    const isExternalUrl = url && url.startsWith("http");

    return (
      <div
        className={cardClassName}
        style={{ ...style, cursor: onClick ? "pointer" : undefined }}
        onClick={() => onClick?.(file)}
      >
        <div
          className={`${styles.fileIcon} ${isSmall ? styles.fileIconSmall : ""}`}
          style={{ color }}
        >
          {icon}
        </div>

        <div className={styles.fileInfo}>
          {isExternalUrl && !onClick ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.fileLink}
            >
              <Tooltip title={fileName}>
                <div className={`${styles.fileName} ${isSmall ? styles.fileNameSmall : ""}`}>
                  {fileName.length > 15
                    ? `${fileName.substring(0, 15)}...`
                    : fileName}
                </div>
              </Tooltip>
            </a>
          ) : (
            <Tooltip title={fileName}>
              <div className={`${styles.fileName} ${isSmall ? styles.fileNameSmall : ""}`}>
                {fileName.length > 15
                  ? `${fileName.substring(0, 15)}...`
                  : fileName}
              </div>
            </Tooltip>
          )}

          {file.status === "uploading" ? (
            <Progress
              percent={file.progress}
              size="small"
              showInfo={false}
              strokeColor="#1677ff"
              className={`${styles.progress} ${isSmall ? styles.progressSmall : ""}`}
            />
          ) : (
            !isSmall && fileSize > 0 && (
              <div className={styles.fileSize}>
                {formatFileSize(fileSize)}
              </div>
            )
          )}

          {file.status === "error" && file.errorMessage && (
            <div
              style={{ fontSize: "11px", color: "#ff4d4f", marginTop: "2px" }}
            >
              {file.errorMessage}
            </div>
          )}
        </div>

        {renderRemoveBtn()}
      </div>
    );
  };

  // ==================== 图片渲染 ====================
  if (fileType === "image") {
    // full / preview 模式：独立展示
    if (imageDisplayMode === "full" || imageDisplayMode === "preview") {
      const containerStyle: React.CSSProperties = { ...style };
      if (imageWidth)
        containerStyle.width =
          typeof imageWidth === "number" ? `${imageWidth}px` : imageWidth;
      if (imageHeight)
        containerStyle.height =
          typeof imageHeight === "number" ? `${imageHeight}px` : imageHeight;

      return (
        <div
          className={`${imageDisplayMode === "full" ? styles.imageFullMode : styles.imagePreviewMode} ${className || ""}`}
          style={containerStyle}
        >
          <ImageWithLoading
            src={url}
            alt={imageAlt || fileName}
            displayMode={imageDisplayMode}
            loading={imageLoading}
            preview={onClick ? false : { src: url }}
            fallback={FALLBACK_IMAGE}
            styles={styles}
          />
          {renderUploadOverlay()}
          {renderRemoveBtn()}
        </div>
      );
    }

    // thumbnail 模式：卡片形式
    return (
      <div
        className={cardClassName}
        style={{ ...style, cursor: onClick ? "pointer" : undefined }}
        onClick={() => onClick?.(file)}
      >
        <ImageWithLoading
          src={url}
          alt={imageAlt || fileName}
          width={size === "small" ? 22 : imageWidth}
          height={size === "small" ? 22 : imageHeight}
          displayMode="thumbnail"
          loading={imageLoading}
          preview={onClick ? false : { src: url }}
          fallback={FALLBACK_IMAGE}
          styles={styles}
          className={isSmall ? styles.imageThumbnailSmall : undefined}
        />
        {renderUploadOverlay()}

        <div className={styles.fileInfo}>
          <Tooltip title={fileName}>
            <div className={`${styles.fileName} ${isSmall ? styles.fileNameSmall : ""}`}>
              {fileName.length > 15
                ? `${fileName.substring(0, 15)}...`
                : fileName}
            </div>
          </Tooltip>
          {!isSmall && Number(file?.size) > 0 && (
            <div className={styles.fileSize}>{formatFileSize(fileSize)}</div>
          )}
        </div>

        {renderRemoveBtn()}
      </div>
    );
  }

  // ==================== 音频渲染（原生播放器，无卡片包裹） ====================
  if (fileType === "audio" && size !== "small") {
    return (
      <div
        className={`file-gallery-audio ${className || ""}`}
        style={{ position: "relative", width: 268, ...style }}
      >
        <audio
          src={url}
          controls
          autoPlay={mediaAutoPlay}
          loop={mediaLoop}
          muted={mediaMuted}
          style={{ width: "100%" }}
          {...audioProps}
        />
        {renderRemoveBtn()}
      </div>
    );
  }

  // ==================== 视频渲染（原生播放器，无卡片包裹） ====================
  if (fileType === "video" && size !== "small") {
    return (
      <div
        className={`file-gallery-video ${className || ""}`}
        style={{ position: "relative", width: 268, ...style }}
      >
        <video
          src={url}
          controls
          autoPlay={mediaAutoPlay}
          loop={mediaLoop}
          muted={mediaMuted}
          poster={mediaPoster}
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            borderRadius: 8,
            background: "#000",
          }}
          {...videoProps}
        />
        {renderRemoveBtn()}
      </div>
    );
  }

  // ==================== 其他文件（附件卡片） ====================
  return renderFileCard();
};

export default FileGallery;
